// src/api/apiAuth.ts
import axios, { AxiosInstance, isAxiosError } from 'axios'
import type { UtenteSessione } from './permessi'

/**
 * Client per l'autenticazione sul backend (VITE_API_URL).
 *
 * Il login vero e proprio avviene via SSO (Microsoft 365 / Google): il backend
 * emette un token Sanctum e reindirizza il frontend a /auth/callback?token=...
 * Qui si gestiscono solo la validazione del token (checktoken) e il logout.
 *
 * Usa un'istanza axios propria, senza l'interceptor 401 di http.ts: un token
 * non valido qui è un esito normale (valid: false), non un motivo per
 * rimbalzare l'utente al login dal mezzo di una pagina.
 */
class ApiAuthService {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      timeout: 30000,
    })

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })
  }

  /** Verifica che il token corrente sia valido. Ritorna i dati utente se ok. */
  async checkToken(): Promise<{ valid: boolean; user?: UtenteSessione }> {
    try {
      const response = await this.api.get('/checktoken')
      return { valid: response.status === 200, user: response.data?.user }
    } catch (error) {
      // 401 => token non valido o scaduto: non è un errore da propagare.
      if (isAxiosError(error) && error.response?.status === 401) {
        return { valid: false }
      }
      throw error
    }
  }

  /** Revoca il token corrente lato server. */
  async logout(): Promise<void> {
    try {
      await this.api.post('/logout')
    } catch (error) {
      // Anche se la revoca lato server fallisce, il frontend procede col logout locale.
      console.warn('Logout backend non riuscito:', error instanceof Error ? error.message : error)
    }
  }
}

export const apiAuth = new ApiAuthService()
